/* ═══════════════════════════════════════════════════════════════════════
 * Lesson text → blocks the lesson player can draw.
 *
 * A section body is Markdown written to a few firm conventions (see the
 * course briefs): an explanation in three paragraphs labelled (PL) / (UA) /
 * (RU), „Typowe błędy…” lists, „Słowa dodatkowe” glosses, dialogues as
 * „— …” lines or „- Kobieta 1: …” script lines, callouts such as „Uwaga,
 * pułapka!”. This turns those conventions into typed blocks, and anything
 * it does not recognise into plain Markdown — so a new convention degrades
 * to readable text instead of disappearing.
 *
 * Derived per request, never stored: no migration, no PARSER_VERSION bump,
 * and a change here reaches every lesson on the next deploy.
 *
 * Runs in the browser too: no lookbehind and no recent built-ins (old
 * Safari — see apps/web/src/routes/student/Markdown.tsx).
 * ═══════════════════════════════════════════════════════════════════════ */
const EXPLAIN = /^\*\*[^*]{2,40}\((PL|UA|RU)\)\.?\*\*\.?\s*/;
const MISTAKES = /^\*\*(Typowe błędy[^*]*?):?\*\*:?\s*/;
const CALLOUT = /^\*\*((?:Uwaga[^*]*)|(?:Najważniejsze do zapamiętania)|(?:Zapamiętaj)|(?:Pamiętaj))([!:.]?)\*\*([!:.]?)\s*/;
const GLOSSES = /^\*\*(Słowa dodatkowe[^*]*?):?\*\*\s*/;
const DASH_LINE = /^— /;
const SCRIPT_LINE = /^- ([^:*\[\]]{1,32}): (.+)$/;
const BOLD_SPEAKER = /^\*\*([^*:]{1,32}):\*\*\s*(.+)$/;
const LONE_BOLD = /^\*\*([^*]{1,120})\*\*\s*$/;
const DIRECTION = /^\[[^\]]{1,80}\]$/;
function paragraphs(lines, offset) {
    const out = [];
    let cur = null;
    lines.forEach((line, i) => {
        if (line.trim() === '' || /^-{3,}\s*$/.test(line.trim())) {
            cur = null;
            return;
        }
        if (!cur) {
            cur = { lines: [], at: [] };
            out.push(cur);
        }
        cur.lines.push(line);
        cur.at.push(offset + i);
    });
    return out;
}
/** "phrase — 🇺🇦 гloss · 🇷🇺 gloss" (also "phrase — note, 🇺🇦 …"). */
export function parseGloss(entry) {
    const text = entry.trim().replace(/\.$/, '');
    const dash = text.indexOf(' — ');
    const pl = (dash >= 0 ? text.slice(0, dash) : text).trim();
    const rest = dash >= 0 ? text.slice(dash + 3) : '';
    const ukAt = rest.indexOf('🇺🇦');
    const ruAt = rest.indexOf('🇷🇺');
    const firstFlag = [ukAt, ruAt].filter((n) => n >= 0).sort((a, b) => a - b)[0];
    const note = (firstFlag === undefined ? rest : rest.slice(0, firstFlag)).replace(/[,;\s]+$/, '').trim();
    const cut = (from, to) => from < 0 ? '' : rest.slice(from + 4, to >= 0 && to > from ? to : undefined).replace(/^\s+|[\s·]+$/g, '').trim();
    return { pl, note, uk: cut(ukAt, ruAt), ru: cut(ruAt, ukAt > ruAt ? ukAt : -1) };
}
/** The gloss in one language from an inline „🇺🇦 x · 🇷🇺 y”; both (as written) for Polish. */
export function pickLang(text, locale) {
    const ukAt = text.indexOf('🇺🇦');
    const ruAt = text.indexOf('🇷🇺');
    if (ukAt < 0 && ruAt < 0)
        return text;
    if (locale !== 'uk' && locale !== 'ru')
        return text;
    const head = text.slice(0, Math.min(...[ukAt, ruAt].filter((n) => n >= 0))).trim();
    const from = locale === 'uk' ? ukAt : ruAt;
    const other = locale === 'uk' ? ruAt : ukAt;
    if (from < 0)
        return text;
    const part = text
        .slice(from + 4, other > from ? other : undefined)
        .replace(/^\s+|[\s·]+$/g, '')
        .trim();
    return head ? `${head} ${part}` : part;
}
function stripLabel(line, re) {
    return line.replace(re, '');
}
function isList(lines) {
    return lines.every((l) => /^\s*[-*] /.test(l) || /^\s{2,}\S/.test(l));
}
/**
 * The blocks of one chunk of text. `used` collects the line numbers each
 * block was built from — the test uses it to prove nothing is dropped.
 */
function blocksOf(paras, used) {
    const out = [];
    const take = (p) => p.at.forEach((n) => used.add(n));
    let i = 0;
    const lastMarkdownPara = { index: -1, text: '' };
    while (i < paras.length) {
        const p = paras[i];
        const first = p.lines[0].trim();
        // ── explanation in three languages ─────────────────────────────────
        if (EXPLAIN.test(first)) {
            const block = { kind: 'explain', pl: '', uk: '', ru: '' };
            while (i < paras.length && EXPLAIN.test(paras[i].lines[0].trim())) {
                const q = paras[i];
                const m = q.lines[0].trim().match(EXPLAIN);
                const parts = [[stripLabel(q.lines[0].trim(), EXPLAIN), ...q.lines.slice(1)].join('\n').trim()];
                const key = m[1] === 'PL' ? 'pl' : m[1] === 'UA' ? 'uk' : 'ru';
                take(q);
                i++;
                // An explanation can run over several paragraphs (a list, „Ważne: …”).
                // Up to the next label they all belong to it; after the last label only
                // what is written in that language does — shared Polish text must stay.
                let j = i;
                while (j < paras.length && !EXPLAIN.test(paras[j].lines[0].trim()) && !stops(paras[j]))
                    j++;
                const nextIsLabel = j < paras.length && EXPLAIN.test(paras[j].lines[0].trim());
                while (i < j && (nextIsLabel || (key !== 'pl' && CYRILLIC.test(paras[i].lines.join(' '))))) {
                    parts.push(paras[i].lines.join('\n'));
                    take(paras[i]);
                    i++;
                }
                const text = parts.join('\n\n');
                block[key] = block[key] ? `${block[key]}\n\n${text}` : text;
            }
            out.push(block);
            continue;
        }
        // ── a paragraph written in Ukrainian or Russian only ───────────────
        if (/^🇺🇦|^🇷🇺/.test(first)) {
            const block = { kind: 'explain', pl: '', uk: '', ru: '' };
            // The plain Polish paragraph right before it is the same thing in Polish.
            const prev = out[out.length - 1];
            // Only plain prose: a rhyme in a blockquote, a list or a table is for everyone.
            if (prev &&
                prev.kind === 'markdown' &&
                lastMarkdownPara.index === out.length - 1 &&
                !/\n\s*\n/.test(prev.md) &&
                !/^\s*(>|[-*] |\||\d+\.\s|#)/.test(prev.md)) {
                block.pl = prev.md;
                out.pop();
            }
            while (i < paras.length && /^🇺🇦|^🇷🇺/.test(paras[i].lines[0].trim())) {
                const q = paras[i];
                const line = q.lines.join('\n').trim();
                // „🇺🇦 *…* · 🇷🇺 *…*” in one paragraph carries both languages.
                const both = line.indexOf('🇺🇦') >= 0 && line.indexOf('🇷🇺') >= 0;
                const parts = both
                    ? [
                        ['uk', pickLang(line, 'uk')],
                        ['ru', pickLang(line, 'ru')],
                    ]
                    : [[line.startsWith('🇺🇦') ? 'uk' : 'ru', line.slice(4).trim()]];
                for (const [key, text] of parts)
                    block[key] = block[key] ? `${block[key]}\n\n${text}` : text;
                take(q);
                i++;
            }
            out.push(block);
            continue;
        }
        // ── typical mistakes ──────────────────────────────────────────────
        if (MISTAKES.test(first)) {
            const title = first.match(MISTAKES)[1].trim();
            const tail = stripLabel(first, MISTAKES).trim();
            let items = p.lines.slice(1);
            take(p);
            i++;
            if (items.length === 0 && i < paras.length && isList(paras[i].lines)) {
                items = paras[i].lines;
                take(paras[i]);
                i++;
            }
            const list = joinListItems(items);
            out.push({ kind: 'mistakes', title, items: tail ? [tail, ...list] : list });
            continue;
        }
        // ── callouts: „Uwaga, pułapka!”, „Najważniejsze do zapamiętania” ──
        if (CALLOUT.test(first)) {
            const m = first.match(CALLOUT);
            const title = (m[1] + (m[2] || m[3] || '')).replace(/:$/, '').trim();
            const rest = [stripLabel(first, CALLOUT), ...p.lines.slice(1)].join('\n').trim();
            take(p);
            i++;
            let md = rest;
            if (!md && i < paras.length && isList(paras[i].lines)) {
                md = paras[i].lines.join('\n');
                take(paras[i]);
                i++;
            }
            out.push({ kind: 'callout', title, md });
            continue;
        }
        // ── glosses: „Słowa dodatkowe (nie musisz ich jeszcze umieć): …” ──
        if (GLOSSES.test(first)) {
            const title = first.match(GLOSSES)[1].trim();
            let rest = [stripLabel(first, GLOSSES), ...p.lines.slice(1)].join(' ').trim();
            let note = '';
            const paren = rest.match(/^\(([^)]*)\)\s*:?\s*/);
            if (paren) {
                note = paren[1].trim();
                rest = rest.slice(paren[0].length);
            }
            rest = rest.replace(/^:\s*/, '');
            take(p);
            i++;
            // Listed one per line instead („- słowo — 🇺🇦 …”).
            if (!rest && i < paras.length && isList(paras[i].lines)) {
                rest = paras[i].lines.map((l) => l.replace(/^\s*[-*]\s+/, '')).join('; ');
                take(paras[i]);
                i++;
            }
            out.push({ kind: 'glosses', title, note, items: splitGlosses(rest).map(parseGloss) });
            continue;
        }
        // ── a table ───────────────────────────────────────────────────────
        if (p.lines.every((l) => l.trim().startsWith('|'))) {
            out.push({ kind: 'table', md: p.lines.join('\n') });
            take(p);
            i++;
            continue;
        }
        // ── dialogue ──────────────────────────────────────────────────────
        const dialogue = dialogueOf(p);
        if (dialogue) {
            let title = dialogue.title;
            // A lone bold line just before („**Scena 1 — Na przystanku**”) is its title.
            const prev = out[out.length - 1];
            if (!title && prev && prev.kind === 'markdown' && lastMarkdownPara.index === out.length - 1) {
                const m = prev.md.trim().match(LONE_BOLD);
                if (m && !prev.md.includes('\n')) {
                    title = m[1].trim();
                    out.pop();
                }
            }
            const block = { kind: 'dialogue', title, lines: dialogue.lines };
            take(p);
            i++;
            // „**Zosia:** …” lines are often one per paragraph; keep a scene together.
            while (i < paras.length) {
                const more = dialogueOf(paras[i]);
                if (!more || more.title || more.format !== dialogue.format)
                    break;
                block.lines.push(...more.lines);
                take(paras[i]);
                i++;
            }
            out.push(block);
            continue;
        }
        // ── a stage direction between scenes: „[pauza 3 s]” ────────────────
        if (p.lines.length === 1 && DIRECTION.test(first)) {
            take(p);
            i++;
            continue;
        }
        // ── anything else stays Markdown; neighbours merge ──────────────────
        const md = p.lines.join('\n');
        const prev = out[out.length - 1];
        if (prev && prev.kind === 'markdown' && lastMarkdownPara.index === out.length - 1) {
            prev.md = `${prev.md}\n\n${md}`;
        }
        else {
            out.push({ kind: 'markdown', md });
        }
        lastMarkdownPara.index = out.length - 1;
        lastMarkdownPara.text = md;
        take(p);
        i++;
    }
    return mergeSplitExplanations(out);
}
/**
 * „**Objaśnienie (PL).** …”, a table, then „**Пояснення (UA).** …” — the
 * same explanation with something in between. Fold the later languages into
 * the first, so each student reads one explanation in their own language and
 * the table after it.
 */
function mergeSplitExplanations(blocks) {
    const out = [];
    for (const b of blocks) {
        if (b.kind === 'explain' && !b.pl && (b.uk || b.ru)) {
            const open = out.find((x) => x.kind === 'explain' && Boolean(x.pl) && !x.uk && !x.ru);
            if (open) {
                open.uk = b.uk;
                open.ru = b.ru;
                continue;
            }
        }
        out.push(b);
    }
    return out;
}
const CYRILLIC = /[\u0400-\u04FF]/;
/** A paragraph that starts something of its own, so an explanation cannot run into it. */
function stops(p) {
    const first = p.lines[0].trim();
    return (MISTAKES.test(first) ||
        CALLOUT.test(first) ||
        GLOSSES.test(first) ||
        /^🇺🇦|^🇷🇺/.test(first) ||
        /^\*\*[^*]{1,60}\*\*:?\s*$/.test(first) ||
        /^\*\*[^*]{1,60}:\*\*/.test(first) ||
        /^#{4,}\s/.test(first) ||
        p.lines.every((l) => l.trim().startsWith('|')) ||
        dialogueOf(p) !== null);
}
/** Split on „;” between entries, but not inside parentheses. */
function splitGlosses(text) {
    const out = [];
    let depth = 0;
    let cur = '';
    for (const ch of text) {
        if (ch === '(')
            depth++;
        if (ch === ')')
            depth = Math.max(0, depth - 1);
        if (ch === ';' && depth === 0) {
            if (cur.trim())
                out.push(cur.trim());
            cur = '';
        }
        else
            cur += ch;
    }
    if (cur.trim())
        out.push(cur.trim());
    return out;
}
/** "- a\n  continued\n- b" → ["a continued", "b"]. */
function joinListItems(lines) {
    const out = [];
    for (const line of lines) {
        if (/^\s*[-*] /.test(line))
            out.push(line.replace(/^\s*[-*] /, '').trim());
        else if (out.length > 0)
            out[out.length - 1] += ` ${line.trim()}`;
        else
            out.push(line.trim());
    }
    return out.filter(Boolean);
}
/** A paragraph that is a dialogue in one of the three formats, or null. */
function dialogueOf(p) {
    let lines = p.lines.map((l) => l.trim());
    let title = '';
    const head = lines[0].match(LONE_BOLD);
    if (head && lines.length > 1) {
        title = head[1].trim();
        lines = lines.slice(1);
    }
    // Stage directions inside a scene stay with it.
    const spoken = lines.filter((l) => !DIRECTION.test(l));
    if (spoken.length === 0)
        return null;
    if (spoken.length >= 2 && spoken.every((l) => DASH_LINE.test(l))) {
        return { title, format: 'dash', lines: spoken.map((l) => ({ speaker: '', text: l.replace(DASH_LINE, '').trim() })) };
    }
    if (spoken.every((l) => SCRIPT_LINE.test(l)) && (spoken.length >= 2 || title)) {
        return {
            title,
            format: 'script',
            lines: spoken.map((l) => {
                const m = l.match(SCRIPT_LINE);
                return { speaker: m[1].trim(), text: m[2].trim() };
            }),
        };
    }
    if (spoken.every((l) => BOLD_SPEAKER.test(l))) {
        return {
            title,
            format: 'bold',
            lines: spoken.map((l) => {
                const m = l.match(BOLD_SPEAKER);
                return { speaker: m[1].trim(), text: m[2].trim() };
            }),
        };
    }
    return null;
}
/** The whole section, cut at its `###` headings. */
export function toChunks(body) {
    return parse(body).chunks;
}
/**
 * Lines of a body no block was built from — empty for every real lesson
 * (the API's parser test asserts it). Stage directions in brackets are
 * deliberately skipped and do not count.
 */
export function droppedLines(body) {
    const { used } = parse(body);
    return body
        .split('\n')
        .map((line, n) => ({ line, n }))
        .filter(({ line, n }) => line.trim() !== '' && !/^-{3,}\s*$/.test(line.trim()) && !used.has(n) && !/^#{3}\s/.test(line))
        .map(({ line }) => line);
}
function parse(body) {
    const lines = body.split('\n');
    const used = new Set();
    const chunks = [];
    let start = 0;
    let title = '';
    const flush = (end) => {
        const slice = lines.slice(start, end);
        if (slice.some((l) => l.trim() !== '') || title) {
            const blocks = blocksOf(paragraphs(slice, start), used);
            const lang = /🇺🇦/.test(title) ? 'uk' : /🇷🇺/.test(title) ? 'ru' : null;
            if (blocks.length > 0 || title)
                chunks.push({ title: title.replace(/\s*🇺🇦|\s*🇷🇺/g, '').trim(), lang, blocks });
        }
    };
    lines.forEach((line, n) => {
        const h = line.match(/^###\s+(.+?)\s*$/);
        if (h) {
            flush(n);
            title = h[1];
            start = n + 1;
        }
    });
    flush(lines.length);
    return { chunks, used };
}
